import { PromoConditionsRowTypes } from 'features/PromoConditions/components/PromoConditionsRow';
import { PromoConditionsValidationProps } from 'features/PromoConditions/PromoConditionsAccordion.types';
import { PlaceBet } from 'shared/interface';

const isNumberInRange = (value?: number | string, min?: number, max?: number): boolean => {
	const numberValue = Number(value);
	if (isNaN(numberValue)) return false;

	if (min != null && max != null) {
		return numberValue >= min && numberValue <= max;
	}

	if (min != null) {
		return numberValue >= min;
	}
	if (max != null) {
		return numberValue <= max;
	}
	return false;
};

const areAllOddsGreaterThan = (data: PlaceBet[], threshold: number | undefined): boolean => {
	if (!data.length || !threshold) return false;

	return data.every((item) => item.outcomeOdds > threshold);
};

const areEventsBeforeDate = (data: any[], timestamp: number): boolean => {
	if (!data.length) return false;
	return data.every((item) => item.eventStart <= timestamp);
};

type Field = 'sportId' | 'eventType' | 'gameType' | 'eventId' | 'categoryId';

const getNormalizedIds = (bet: PlaceBet): [number, number] => {
	const categoryId = Number(bet.categoryId ?? -1);
	const sportId = Number(bet.sportId ?? -1);
	return [categoryId, sportId];
};

const validateAllIn = (allowed: number[], bets: PlaceBet[], field?: Field): boolean => {
	if (!allowed.length) return false;

	return bets.every((bet) => {
		if (field) {
			return allowed.includes(bet[field]);
		}
		const [categoryId, sportId] = getNormalizedIds(bet);
		return allowed.includes(categoryId) || allowed.includes(sportId);
	});
};

const validateNoneIn = (forbidden: number[], bets: PlaceBet[], field?: Field): boolean => {
	if (!forbidden.length) return false;

	return bets.every((bet) => {
		if (field) {
			return !forbidden.includes(bet[field]);
		}
		const [categoryId, sportId] = getNormalizedIds(bet);
		return !forbidden.includes(categoryId) && !forbidden.includes(sportId);
	});
};

const validateCouponType = (slipBetTypes: number[] | undefined, numberOfOutcomes: number): boolean => {
	if (!slipBetTypes) {
		return false;
	}

	const hasAko = slipBetTypes.includes(0);
	const hasSolo = slipBetTypes.includes(100);

	if (hasAko && hasSolo) {
		return numberOfOutcomes >= 1;
	}

	if (hasSolo) {
		return numberOfOutcomes === 1;
	}

	if (hasAko) {
		return numberOfOutcomes >= 2;
	}

	return false;
};

const validateOneIn = (required: number[], bets: PlaceBet[], field?: Field): boolean => {
	if (!required.length) return false;

	return bets.some((bet) => {
		if (field) {
			return required.includes(bet[field]);
		}
		const [categoryId, sportId] = getNormalizedIds(bet);
		return required.includes(categoryId) || required.includes(sportId);
	});
};

export function promoConditionsValidation(props: PromoConditionsValidationProps) {
	const { allIn, noneIn, oneIn, odds, stake, outcomes: outcomeConditions, slipBetTypes } = props.conditionsConfig;
	const validations = [
		// --- allIn ---
		{
			condition: allIn.categoryIds.length > 0,
			key: 'isAllInCategoryIdValid',
			validate: () => validateAllIn(allIn.categoryIds, props.outcomes),
		},
		{
			condition: allIn.eventIds.length > 0,
			key: 'isAllInEventIdValid',
			validate: () => validateAllIn(allIn.eventIds, props.outcomes, PromoConditionsRowTypes.EVENT_ID),
		},
		{
			condition: allIn.eventTypes.length > 0,
			key: 'isAllInEventTypeValid',
			validate: () => validateAllIn(allIn.eventTypes, props.outcomes, PromoConditionsRowTypes.EVENT_TYPE),
		},
		{
			condition: allIn.gameTypes.length > 0,
			key: 'isAllInGameTypeValid',
			validate: () => validateAllIn(allIn.gameTypes, props.outcomes, PromoConditionsRowTypes.GAME_TYPE),
		},

		// --- noneIn ---
		{
			condition: noneIn.categoryIds.length > 0,
			key: 'isNoneInCategoryValid',
			validate: () => validateNoneIn(noneIn.categoryIds, props.outcomes),
		},
		{
			condition: noneIn.eventIds.length > 0,
			key: 'isNoneInEventIdValid',
			validate: () => validateNoneIn(noneIn.eventIds, props.outcomes, PromoConditionsRowTypes.EVENT_ID),
		},
		{
			condition: noneIn.eventTypes.length > 0,
			key: 'isNoneInEventTypeValid',
			validate: () => validateNoneIn(noneIn.eventTypes, props.outcomes, PromoConditionsRowTypes.EVENT_TYPE),
		},
		{
			condition: noneIn.gameTypes.length > 0,
			key: 'isNoneInGameTypeValid',
			validate: () => validateNoneIn(noneIn.gameTypes, props.outcomes, PromoConditionsRowTypes.GAME_TYPE),
		},

		// --- oneIn ---
		{
			condition: oneIn.categoryIds.length > 0,
			key: 'isOneInCategoryValid',
			validate: () => validateOneIn(oneIn.categoryIds, props.outcomes),
		},
		{
			condition: oneIn.eventTypes.length > 0,
			key: 'isOneInEventTypeValid',
			validate: () => validateOneIn(oneIn.eventTypes, props.outcomes, PromoConditionsRowTypes.EVENT_TYPE),
		},
		{
			condition: oneIn.gameTypes.length > 0,
			key: 'isOneInGameTypeValid',
			validate: () => validateOneIn(oneIn.gameTypes, props.outcomes, PromoConditionsRowTypes.GAME_TYPE),
		},

		// --- odds ---
		{
			condition: odds.minCoupon !== undefined || odds.maxCoupon !== undefined,
			key: 'isCouponOddsValid',
			validate: () => isNumberInRange(props.course, odds.minCoupon, odds.maxCoupon),
		},
		{
			condition: odds.minEvent !== undefined,
			key: 'isEventOddsValid',
			validate: () => areAllOddsGreaterThan(props.outcomes, odds.minEvent),
		},

		// --- stake ---
		{
			condition: stake.min !== undefined || stake.max !== undefined,
			key: 'isStakeValid',
			validate: () => isNumberInRange(props.bets.stake, stake.min, stake.max),
		},

		// --- number of bets ---
		{
			condition: outcomeConditions.min !== undefined || outcomeConditions.max !== undefined,
			key: 'isNumberOfBetsValid',
			validate: () => isNumberInRange(props.outcomes.length, outcomeConditions.min, outcomeConditions.max),
		},

		// --- time ---
		{
			condition: props.selectedData?.validTo !== undefined,
			key: 'isTimeValid',
			validate: () => areEventsBeforeDate(props.outcomes, props.selectedData.validTo),
		},
		// --- coupon type ---
		{
			condition: slipBetTypes?.length !== 0,
			key: 'isCouponValid',
			validate: () => validateCouponType(slipBetTypes, props.bets.outcomes.length),
		},
	];
	return validations;
}
